import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { VideoCard } from "@/components/VideoCard";
import { Search, Loader2, Clapperboard, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["", "completed", "processing", "failed", "uploaded"];
const PAGE_SIZES = [10, 20, 30, 50, 100];

export default function GeneratedVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const filterParams = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [search, status, dateFrom, dateTo]);

  // Reset to first page whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [search, status, dateFrom, dateTo, pageSize]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/videos", { params: { ...filterParams(), page, page_size: pageSize } });
      setVideos(data.items);
      setTotal(data.total);
    } catch (e) { apiErr(e); } finally { setLoading(false); }
  }, [filterParams, page, pageSize]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  // Keep the page within range after deletions or filtering.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/videos/export", {
        params: { ...filterParams(), format },
        responseType: "blob",
      });
      const ext = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `video_report.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report downloaded (${ext.toUpperCase()})`);
    } catch (e) {
      toast.error(apiErr(e, "Export failed"));
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-6" data-testid="generated-videos">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Library</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mt-1">Generated Videos</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => exportReport("csv")} disabled={!!exporting}
            data-testid="export-csv-button"
            className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            CSV
          </button>
          <button type="button" onClick={() => exportReport("xlsx")} disabled={!!exporting}
            data-testid="export-xlsx-button"
            className="inline-flex items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60">
            {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel
          </button>
          <button type="button" onClick={() => exportReport("pdf")} disabled={!!exporting}
            data-testid="export-pdf-button"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            PDF Report
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="video-search-input"
            placeholder="Search doctor, MR, city, specialty…"
            className="h-11 w-full rounded-md border border-stone-300 bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="video-status-filter"
          className="h-11 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 capitalize">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="video-date-from-filter"
          title="From date"
          className="h-11 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="video-date-to-filter"
          title="To date"
          className="h-11 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} data-testid="video-page-size-filter"
          title="Videos per page"
          className="h-11 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>
      ) : videos.length === 0 ? (
        <div className="rounded-lg border border-stone-200 bg-white p-12 text-center">
          <Clapperboard className="h-10 w-10 mx-auto text-stone-300" />
          <p className="mt-3 text-stone-600 font-medium">No videos found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {videos.map((v) => (
              <VideoCard key={v.id} video={v} showMr onChanged={load} onDeleted={(id) => { setVideos((p) => p.filter((x) => x.id !== id)); setTotal((t) => Math.max(0, t - 1)); }} />
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2" data-testid="video-pagination">
            <p className="text-sm text-stone-600">
              Showing <span className="font-medium text-stone-900">{rangeStart}</span>–<span className="font-medium text-stone-900">{rangeEnd}</span> of{" "}
              <span className="font-medium text-stone-900">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                data-testid="video-prev-page"
                className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-sm text-stone-600">Page <span className="font-medium text-stone-900">{page}</span> of {totalPages}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                data-testid="video-next-page"
                className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
